import {useState} from 'react';
import {Button, Dropdown, Loader, Modal} from '@shipwell/shipwell-ui';
import pluralize from 'pluralize';
import {useQueryClient} from '@tanstack/react-query';
import {
  BaseShipment,
  ShipmentDocumentMetadataTypeEnum,
  ShippingDashboardRow
} from '@shipwell/backend-core-singlerequestparam-sdk';
import {AxiosResponse} from 'axios';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {BulkActionLineItemButton} from 'App/containers/Dashboard/components/BulkActions/Dropdown/DropdownLineItemButton';
import {
  CurrencyOfRecordTooltipContent,
  NonParcelShipmentsTooltipContent
} from 'App/containers/Dashboard/components/BulkActions/Dropdown/DropdownLineItemButton/TooltipContent';
import ChangeShipmentStatus from 'App/formComponents/forms/changeShipmentStatus';
import WithStatusToasts, {WithStatusToastProps} from 'App/components/withStatusToasts';
import {createBulkOperation, getBulkOperation} from 'App/api/bulkOperations';
import {
  bulkOperationTypeMessages,
  BULK_OPERATION_TYPE_CHANGE_SHIPMENT_STATUS,
  BULK_OPERATION_TYPE_EDIT_SHIPMENT_DATES,
  BULK_OPERATION_TYPE_PUSH_TO_ROUTING_GUIDE,
  BULK_OPERATION_TYPE_TENDER_TO_CARRIER,
  BULK_OPERATION_TYPE_ARCHIVE_SHIPMENT,
  EMAIL_DOCUMENT,
  PRINT_DOCUMENT
} from 'App/containers/Dashboard/utils/constants';
import {
  BulkOperation,
  BulkOperationGetResponse,
  BulkOperationPostResponse
} from 'App/containers/Dashboard/utils/DashboardUtilTypes';
import {SHIPMENTS_DASHBOARD_QUERY_KEY} from 'App/data-hooks/queryKeys';
import useGetShipmentsDownloadCsvMutation, {
  GetShipmentsDownloadCsvMutationOptions
} from 'App/containers/Dashboard/components/ShipmentsCsvDownload/hooks/useGetShipmentsDownloadCsvMutation';
import prepareAndDownloadFile from 'App/utils/fileUtilsTyped';
import useToggle from 'App/utils/hooks/useToggle';
import EditShipmentDates from 'App/formComponents/forms/editShipmentDates';
import ShipmentDocument from 'App/containers/Dashboard/components/ShipmentDocument';
import ExecuteRoutingGuide from 'App/containers/routingGuides/execute';
import {ROUTING_GUIDE_SUCCESS_ON_EXECUTION} from 'App/containers/workflows/workflowConstants';
import {isSamePreferredCurrency} from 'App/containers/Dashboard/components/BulkActions/Dropdown/DropdownLineItemButton/utils';
import BulkTenderRequestForm from 'App/containers/Dashboard/components/BulkActions/bulkTenderRequest';
import {PermissionsFallback} from 'App/components/permissions/PermissionsFallback';
import {CREATE_SHIPMENTS_USER_PERMISSION} from 'App/components/permissions/PermissionsFallback/constants';

enum ModalKindEnums {
  changeShipmentStatus = 'CHANGE_SHIPMENT_STATUS',
  downloadShipments = 'DOWNLOAD_SHIPMENTS',
  editShipmentDates = 'EDIT_SHIPMENT_DATES',
  pushToRoutingGuide = 'PUSH_TO_ROUTING_GUIDE',
  tenderToCarrier = 'TENDER_TO_CARRIER',
  emailBol = 'EMAIL_BOL',
  printBol = 'PRINT_BOL',
  printShippingLabel = 'PRINT_SHIPPING_LABEL',
  cancelShipment = 'CANCEL_SHIPMENT'
}

const modalTitles = {
  [ModalKindEnums.changeShipmentStatus]: 'Change Shipment Status',
  // downloadShipments does not get a modal, but needs a value here to silence TS
  [ModalKindEnums.downloadShipments]: '',
  [ModalKindEnums.editShipmentDates]: 'Edit Shipment Dates',
  [ModalKindEnums.pushToRoutingGuide]: 'Push to Routing Guide',
  [ModalKindEnums.tenderToCarrier]: 'Tender to Carrier',
  [ModalKindEnums.emailBol]: 'Email BOLs',
  [ModalKindEnums.printBol]: 'Print BOLs',
  [ModalKindEnums.printShippingLabel]: 'Print Shipping Labels',
  [ModalKindEnums.cancelShipment]: 'Cancel Shipment'
};

export const ShipmentBulkActionsDropdown = WithStatusToasts(
  ({
    selectedShipments,
    onSuccess,
    setSuccess,
    setError,
    toggleShowImportShipmentModal,
    columnExportIds
  }: {
    selectedShipments: ShippingDashboardRow[];
    onSuccess?: () => void;
    toggleShowImportShipmentModal: () => void;
    columnExportIds: string[];
  } & WithStatusToastProps) => {
    const [modalKind, setModalKind] = useState<ModalKindEnums>();
    const [loadingMessage, setLoadingMessage] = useState('');
    const [performingBulkAction, togglePerformingBulkAction] = useToggle();
    const {wfaBulkActionShipmentStatusChange, wfaBulkActions} = useFlags<{
      wfaBulkActions: boolean;
      wfaBulkActionShipmentStatusChange: boolean;
    }>();
    const queryClient = useQueryClient();
    const selectedShipmentIds = selectedShipments.map((shipment) => shipment.id);
    const downloadFileName = 'shipments.csv';
    const shipmentsCsvDownloadMutationOptions: GetShipmentsDownloadCsvMutationOptions = {
      onMutate: () => setLoadingMessage('Downloading CSV...'),
      onSettled: () => setLoadingMessage(''),
      onSuccess: (data) => {
        prepareAndDownloadFile(data as AxiosResponse, downloadFileName);
        onSuccess?.();
      },
      onError: ({response}) => {
        setError('Error downloading CSV', response?.data.error_message || 'An unknown error occurred.');
        console.error(response?.data);
      }
    };

    const {mutate: downloadShipments} = useGetShipmentsDownloadCsvMutation({
      shipmentIds: selectedShipmentIds,
      columnNames: columnExportIds,
      options: shipmentsCsvDownloadMutationOptions
    });
    let bulkOperationInterval: NodeJS.Timer | undefined;
    function pollBulkOperation(bulkOperationId: string, bulkOperationType: BulkOperation) {
      let pollCount = 0;
      let hasShownSuccess = false;
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      bulkOperationInterval = setInterval(async () => {
        try {
          pollCount++;
          const response = (await getBulkOperation(bulkOperationId)) as BulkOperationGetResponse;
          if (
            response.data.status === 'FINISHED' ||
            (response.data.status === 'PROCESSING' &&
              [BULK_OPERATION_TYPE_PUSH_TO_ROUTING_GUIDE, BULK_OPERATION_TYPE_TENDER_TO_CARRIER].includes(
                bulkOperationType
              ))
          ) {
            //routing guides may stay in processing for a long time because of timers, so we count that as complete after 30 seconds
            if (response.data.status === 'FINISHED' || pollCount > 15) {
              clearInterval(bulkOperationInterval);
              if (
                [
                  BULK_OPERATION_TYPE_EDIT_SHIPMENT_DATES,
                  BULK_OPERATION_TYPE_CHANGE_SHIPMENT_STATUS,
                  BULK_OPERATION_TYPE_ARCHIVE_SHIPMENT
                ].includes(bulkOperationType)
              ) {
                void queryClient.invalidateQueries([SHIPMENTS_DASHBOARD_QUERY_KEY]);
              }
            }
            if (response.data.failed > 0) {
              const failedShipments = response.data.resource_statuses
                .filter((resource) => resource.status === 'ERROR')
                .map((resource) => {
                  return {
                    id: resource.resource_id,
                    reference_id: selectedShipments.find((shipment) => shipment.id === resource.resource_id)
                      ?.reference_id
                  };
                });
              const {errorTitle, errorDetails} = bulkOperationTypeMessages[bulkOperationType];
              setError(
                errorTitle,
                <div>
                  <div>{`${errorDetails} ${pluralize('shipment', failedShipments.length)}.`}</div>
                  <div>
                    {failedShipments.map((shipment) => {
                      return (
                        <div key={shipment.id}>
                          <a href={`/shipments/${shipment.id}`} target="_blank" rel="noreferrer">
                            {shipment.reference_id}
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>,
                'top-right',
                {delay: null}
              );
            } else {
              if (!hasShownSuccess) {
                hasShownSuccess = true;
                const {successTitle, successDetails} = bulkOperationTypeMessages[bulkOperationType];
                setSuccess(successTitle, `${successDetails} ${pluralize('shipment', response.data.total, true)}.`);
              }
            }
          }
        } catch (error) {
          console.error(error);
        }
      }, 3000);
    }
    async function cancelBulkShipments(selectedShipmentIds: string[]) {
      try {
        if (selectedShipmentIds) {
          const bulkOperationBody = {
            resource_type: 'shipment',
            workflow: {
              name: 'Bulk op',
              start_events: [
                {
                  step_id: 'STEP_1',
                  trigger: {
                    trigger_type: 'NONE',
                    outputs: [
                      {name: 'shipment_id', data_type: 'String', push_to_list: null},
                      {name: 'shipment', data_type: 'Shipment', push_to_list: null}
                    ]
                  },
                  next_step: {
                    step_id: 'STEP_2'
                  }
                }
              ],
              end_events: [
                {
                  step_id: 'STEP_END',
                  should_terminate: true
                }
              ],
              actions: [
                {
                  action_id: 'MODIFY_SHIPMENT_STATUS',
                  step_id: 'STEP_2',
                  attached_triggers: [
                    {
                      trigger: {
                        trigger_type: 'NONE'
                      },
                      next_step_id: 'STEP_END'
                    }
                  ],
                  inputs: [
                    {
                      name: 'shipment_id',
                      data_type: 'String',
                      is_required: true
                    }
                  ],
                  params: [
                    {
                      name: 'new_status',
                      value: 'cancelled'
                    }
                  ]
                }
              ]
            },
            criteria: {
              id: {
                $in: selectedShipmentIds
              }
            }
          };
          const response = (await createBulkOperation(bulkOperationBody)) as BulkOperationPostResponse;
          setSuccess(
            `Shipping cancellation has been submitted for ${pluralize('shipment', selectedShipmentIds.length, true)}.`
          );
          pollBulkOperation(response.data.id, BULK_OPERATION_TYPE_CHANGE_SHIPMENT_STATUS);
        }
      } catch (error) {
        console.error(`Error cancelling ${pluralize('shipment', selectedShipmentIds.length)}.`, error);
        setError(
          'Changes Not Saved',
          `Unable to cancel the ${pluralize('shipment', selectedShipmentIds.length)} you selected.`
        );
      }
    }

    async function archiveBulkShipments(selectedShipmentIds: string[]) {
      try {
        if (selectedShipmentIds) {
          const bulkOperationBody = {
            resource_type: 'shipment',
            workflow: {
              name: 'Bulk op',
              start_events: [
                {
                  step_id: 'STEP_1',
                  trigger: {
                    trigger_type: 'NONE',
                    outputs: [
                      {name: 'shipment_id', data_type: 'String', push_to_list: null},
                      {name: 'shipment', data_type: 'Shipment', push_to_list: null}
                    ]
                  },
                  next_step: {
                    step_id: 'STEP_2'
                  }
                }
              ],
              end_events: [
                {
                  step_id: 'STEP_END',
                  should_terminate: true
                }
              ],
              actions: [
                {
                  action_id: 'ARCHIVE_SHIPMENT',
                  step_id: 'STEP_2',
                  attached_triggers: [
                    {
                      trigger: {
                        trigger_type: 'NONE'
                      },
                      next_step_id: 'STEP_END'
                    }
                  ],
                  inputs: [
                    {
                      name: 'shipment_id',
                      data_type: 'String',
                      is_required: true
                    }
                  ],
                  params: []
                }
              ]
            },
            criteria: {
              id: {
                $in: selectedShipmentIds
              }
            }
          };
          const response = (await createBulkOperation(bulkOperationBody)) as BulkOperationPostResponse;
          setSuccess('Bulk Action Started!', `Archiving ${pluralize('shipment', selectedShipmentIds.length, true)}.`);
          pollBulkOperation(response.data.id, BULK_OPERATION_TYPE_ARCHIVE_SHIPMENT);
        }
      } catch (error) {
        console.error(`Error archiving ${pluralize('shipment', selectedShipmentIds.length)} status.`, error);
        setError(
          'Changes Not Saved',
          `Your changes could not be applied to the ${pluralize('shipment', selectedShipmentIds.length)} you selected.`
        );
      }
    }

    if (selectedShipments.length === 0) {
      return (
        <PermissionsFallback permissions={[CREATE_SHIPMENTS_USER_PERMISSION]}>
          <Button
            isCompact
            variant="tertiary"
            iconName="AddCircleOutlined"
            onClick={() => toggleShowImportShipmentModal()}
          >
            Import
          </Button>
        </PermissionsFallback>
      );
    }

    return (
      <>
        <Dropdown title="Shipment Bulk Actions" indicator variant="primary">
          {({onClick}: {onClick: () => void}) => [
            <BulkActionLineItemButton
              key={ModalKindEnums.changeShipmentStatus}
              isDisabled={false}
              isVisible={wfaBulkActionShipmentStatusChange}
              onClick={() => {
                onClick();
                setModalKind(ModalKindEnums.changeShipmentStatus);
              }}
              disabledReason={NonParcelShipmentsTooltipContent('changeShipmentStatus')}
            >
              Change Shipment Status
            </BulkActionLineItemButton>,
            <BulkActionLineItemButton
              key={ModalKindEnums.downloadShipments}
              onClick={() => {
                downloadShipments({});
                setModalKind(undefined);
              }}
              isDisabled={false}
              isVisible
            >
              Download Shipments
            </BulkActionLineItemButton>,
            <BulkActionLineItemButton
              key="editShipmentDates"
              isDisabled={false}
              isVisible={wfaBulkActions}
              onClick={() => {
                setModalKind(ModalKindEnums.editShipmentDates);
                togglePerformingBulkAction();
                onClick();
              }}
              disabledReason={NonParcelShipmentsTooltipContent('editShipmentDates')}
            >
              Edit Shipment Dates
            </BulkActionLineItemButton>,
            <BulkActionLineItemButton
              key="emailBol"
              isVisible={wfaBulkActions}
              isDisabled={false}
              onClick={() => {
                setModalKind(ModalKindEnums.emailBol);
                onClick();
              }}
            >
              Email BOL
            </BulkActionLineItemButton>,
            <BulkActionLineItemButton
              key="printBol"
              isVisible={wfaBulkActions}
              isDisabled={false}
              onClick={() => {
                setModalKind(ModalKindEnums.printBol);
                onClick();
              }}
            >
              Print BOL
            </BulkActionLineItemButton>,
            <BulkActionLineItemButton
              key="printShippingLabel"
              isVisible={wfaBulkActions}
              isDisabled={false}
              onClick={() => {
                setModalKind(ModalKindEnums.printShippingLabel);
                onClick();
              }}
            >
              Print Shipping Label
            </BulkActionLineItemButton>,
            <BulkActionLineItemButton
              key="pushToRoutingGuide"
              isVisible={wfaBulkActions}
              isDisabled={false}
              onClick={() => {
                setModalKind(ModalKindEnums.pushToRoutingGuide);
                togglePerformingBulkAction();
                onClick();
              }}
              disabledReason={NonParcelShipmentsTooltipContent('pushToRoutingGuide')}
            >
              Push to Routing Guide
            </BulkActionLineItemButton>,
            <BulkActionLineItemButton
              isVisible={wfaBulkActions}
              isDisabled={!isSamePreferredCurrency(selectedShipments)}
              onClick={() => {
                setModalKind(ModalKindEnums.tenderToCarrier);
                togglePerformingBulkAction();
                onClick();
              }}
              key="tenderToCarrier"
              disabledReason={
                !isSamePreferredCurrency(selectedShipments) ? CurrencyOfRecordTooltipContent() : undefined
              }
            >
              Tender to Carrier
            </BulkActionLineItemButton>,
            <BulkActionLineItemButton
              isVisible={wfaBulkActions}
              isDisabled={false}
              onClick={() => {
                togglePerformingBulkAction();
                void archiveBulkShipments(selectedShipmentIds);
                onClick();
              }}
              key="archiveShipment"
            >
              Archive {pluralize('Shipment', selectedShipmentIds.length)}
            </BulkActionLineItemButton>,
            <BulkActionLineItemButton
              key="cancelShipment"
              isVisible
              isDisabled={false}
              onClick={() => {
                togglePerformingBulkAction();
                void cancelBulkShipments(selectedShipmentIds);
                onClick();
              }}
            >
              <span className="text-sw-destroy">Cancel {pluralize('Shipment', selectedShipmentIds.length)}</span>
            </BulkActionLineItemButton>
          ]}
        </Dropdown>
        <Modal
          show={Boolean(modalKind)}
          title={modalKind ? modalTitles[modalKind] : null}
          footerComponent={null}
          onClose={() => {
            togglePerformingBulkAction();
            setModalKind(undefined);
          }}
          bodyClassName="overflow-auto"
          bodyVariant="disableOverflowScroll"
        >
          {modalKind === ModalKindEnums.changeShipmentStatus ? (
            <ChangeShipmentStatus
              selectedShipmentIds={selectedShipmentIds}
              onCancel={() => setModalKind(undefined)}
              onSubmitSuccess={(bulkOperationId: string) => {
                setSuccess(
                  'Shipment Bulk Actions Submitted!',
                  `Shipping status changes have been submitted for ${pluralize(
                    'shipment',
                    selectedShipments.length,
                    true
                  )}.`
                );
                pollBulkOperation(bulkOperationId, BULK_OPERATION_TYPE_CHANGE_SHIPMENT_STATUS);
                onSuccess?.();
                setModalKind(undefined);
              }}
              setError={setError}
            />
          ) : modalKind === ModalKindEnums.editShipmentDates ? (
            <EditShipmentDates
              selectedShipmentIds={selectedShipmentIds}
              onCancel={() => {
                setModalKind(undefined);
                togglePerformingBulkAction();
              }}
              onSubmitSuccess={(bulkOperationId: string) => {
                setSuccess(
                  'Shipment Bulk Actions Submitted!',
                  `Shipping date changes have been submitted for ${pluralize(
                    'shipment',
                    selectedShipments.length,
                    true
                  )}.`
                );
                pollBulkOperation(bulkOperationId, BULK_OPERATION_TYPE_EDIT_SHIPMENT_DATES);
                setModalKind(undefined);
                togglePerformingBulkAction();
                onSuccess?.();
              }}
              setError={setError}
            />
          ) : modalKind === ModalKindEnums.emailBol ? (
            <ShipmentDocument
              mode={EMAIL_DOCUMENT}
              shipments={selectedShipments as BaseShipment[]}
              onCancel={() => setModalKind(undefined)}
              onSuccess={onSuccess}
              documentType={ShipmentDocumentMetadataTypeEnum.Bol}
            />
          ) : modalKind === ModalKindEnums.printBol ? (
            <ShipmentDocument
              mode={PRINT_DOCUMENT}
              shipments={selectedShipments as BaseShipment[]}
              onCancel={() => setModalKind(undefined)}
              onSuccess={onSuccess}
              documentType={ShipmentDocumentMetadataTypeEnum.Bol}
            />
          ) : modalKind === ModalKindEnums.printShippingLabel ? (
            <ShipmentDocument
              mode={PRINT_DOCUMENT}
              shipments={selectedShipments as BaseShipment[]}
              onCancel={() => setModalKind(undefined)}
              onSuccess={onSuccess}
              documentType={ShipmentDocumentMetadataTypeEnum.ShippingLabel}
            />
          ) : modalKind === ModalKindEnums.pushToRoutingGuide ? (
            <ExecuteRoutingGuide
              selectedShipments={performingBulkAction ? selectedShipmentIds : null}
              onCancel={() => {
                setModalKind(undefined);
                togglePerformingBulkAction();
              }}
              onSubmitSuccess={(bulkOperationId: string) => {
                if (performingBulkAction) {
                  setSuccess(
                    'Shipment Bulk Actions Submitted!',
                    `Routing guides have been submitted for ${pluralize('shipment', selectedShipments.length, true)}.`
                  );
                  pollBulkOperation(bulkOperationId, BULK_OPERATION_TYPE_PUSH_TO_ROUTING_GUIDE);
                } else {
                  setSuccess('Your routing guide is running!', ROUTING_GUIDE_SUCCESS_ON_EXECUTION);
                }
                setModalKind(undefined);
                togglePerformingBulkAction();
                onSuccess?.();
              }}
            />
          ) : modalKind === ModalKindEnums.tenderToCarrier ? (
            <BulkTenderRequestForm
              selectedShipmentsById={selectedShipments.reduce<Record<string, unknown>>((shipmentObj, shipment) => {
                const shipmentId = shipment.id;
                shipmentObj[shipmentId] = shipment;
                return shipmentObj;
              }, {})}
              onClose={() => {
                setModalKind(undefined);
                togglePerformingBulkAction();
              }}
              onSuccess={() => {
                setSuccess(
                  'Tender Request Sent!',
                  `Tender requests have been sent for ${pluralize('shipment', selectedShipments.length, true)}.`
                );
                onSuccess?.();
              }}
              onBulkSuccess={(bulkOperationId: string) => {
                pollBulkOperation(bulkOperationId, BULK_OPERATION_TYPE_TENDER_TO_CARRIER);
                onSuccess?.();
              }}
            />
          ) : null}
        </Modal>

        <Loader show={Boolean(loadingMessage)}>{loadingMessage}</Loader>
      </>
    );
  }
);
